/**
 * Оборачиваемость.
 *
 * Вопросы: за сколько дней продаётся запас и как это меняется; какие
 * позиции лежат дольше всех; какие приносят меньше всего прибыли на рубль,
 * вложенный в склад.
 *
 * Считается только по месяцам, где есть и снимок остатков, и себестоимость
 * продаж: средний запас без продаж или продажи без запаса дают не
 * оборачиваемость, а деление на ноль. Поэтому период здесь — месяц,
 * а не произвольный диапазон: сумма средних запасов за год не равна
 * среднему запасу за год.
 *
 *   дни запаса = средний запас ÷ себестоимость продаж месяца × дней в месяце
 *   GMROI      = валовая прибыль ÷ средний запас (позиции без себестоимости
 *                не входят в прибыль, иначе вся их выручка стала бы прибылью)
 */

import { useState } from 'react'
import { num, useReport } from '../../finance/data'
import { BarRows, ColumnChart } from '../../finance/charts'
import { Caveat, DataNotes, Kpi, Panel, Segmented, SkeletonColumns, SkeletonRows } from '../../finance/parts'
import { usePublishFreshness } from '../../finance/freshness'
import { monthLabel, shortMonth, todayIso } from '../../finance/period'
import { money } from '../../finance/sections/Overview'
import { formatNumber, pluralize } from '../../format'
import { labelOr } from '../parts'
import { coverageOf, daysIn, lastSalesMonth, lastTurnoverMonth, monthEnd, shiftMonth, useStockMonths } from './queries'

const days = (value: number) => `${formatNumber(value < 10 ? Math.round(value * 10) / 10 : Math.round(value))} дн.`
const ratio = (value: number) => new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)

export function Turnover() {
  const months = useStockMonths()
  const coverage = coverageOf(months)
  const choices = coverage.filter((item) => item.stock > 0 && item.cogs > 0).map((item) => item.month).reverse().slice(0, 12)
  // Текущий месяц не закончен: продажи и снимки за него неполные. По умолчанию —
  // последний закончившийся, текущий можно выбрать сам.
  const thisMonth = `${todayIso().slice(0, 7)}-01`
  const latest = choices.find((item) => item < thisMonth) ?? lastTurnoverMonth(coverage)
  const [chosen, setChosen] = useState<string | null>(null)
  const month = chosen && choices.includes(chosen) ? chosen : latest

  const items = useReport(
    'stock_turnover',
    month ? { period: { from: month, to: monthEnd(month) }, group_by: ['nomenclature'], metrics: ['revenue', 'cogs', 'avg_stock_value'] } : null,
  )
  usePublishFreshness([months])
  const [order, setOrder] = useState<'slow' | 'fast' | 'lowgm'>('slow')

  const byMonth = new Map(coverage.map((item) => [item.month, item]))
  const at = month ? byMonth.get(month) : undefined
  const previous = month ? byMonth.get(shiftMonth(month, -1)) : undefined
  const daysOf = (item: { stock: number; cogs: number; month: string } | undefined) =>
    item && item.stock > 0 && item.cogs > 0 ? (item.stock / item.cogs) * daysIn(item.month) : null

  const rows = (items.data?.rows ?? [])
    .map((row) => ({
      key: String(row.nomenclature_guid ?? row.nomenclature ?? ''),
      label: labelOr(row.nomenclature, 'Позиция не указана'),
      stock: num(row.avg_stock_value),
      cogs: num(row.cogs),
      revenue: num(row.revenue),
    }))
    .filter((row) => row.stock > 0)
  const selling = rows.filter((row) => row.cogs > 0)
  const idle = rows.filter((row) => row.cogs <= 0)
  const idleStock = idle.reduce((sum, row) => sum + row.stock, 0)
  const stockTotal = rows.reduce((sum, row) => sum + row.stock, 0)
  const profit = selling.reduce((sum, row) => sum + (row.revenue - row.cogs), 0)
  const gmroi = stockTotal > 0 && items.status === 'ready' ? profit / stockTotal : null
  const D = month ? daysIn(month) : 30
  const withMetrics = selling.map((row) => ({ ...row, days: (row.stock / row.cogs) * D, gmroi: (row.revenue - row.cogs) / row.stock }))
  const sorted =
    order === 'slow'
      ? [...withMetrics].sort((a, b) => b.days - a.days)
      : order === 'fast'
        ? [...withMetrics].sort((a, b) => a.days - b.days)
        : [...withMetrics].sort((a, b) => a.gmroi - b.gmroi)

  const trendMonths = month ? Array.from({ length: 12 }, (_, index) => shiftMonth(month, index - 11)) : []
  const lastSales = lastSalesMonth(coverage)
  const stockMonths = coverage.filter((item) => item.stock > 0).map((item) => item.month)

  const noMonth = months.status === 'ready' && !month
  const noMonthHint =
    stockMonths.length === 0
      ? 'Снимков остатков за последние 24 месяца нет.'
      : !lastSales
        ? 'Продаж за последние 24 месяца нет.'
        : `Продажи загружены по ${monthLabel(lastSales)}, снимки остатков — с ${monthLabel(stockMonths[0]!)} по ${monthLabel(stockMonths[stockMonths.length - 1]!)}: общего месяца с себестоимостью нет.`

  const state = noMonth ? 'ready' : months.status === 'ready' ? items.status : months.status
  const currentDays = daysOf(at)
  const previousDays = daysOf(previous)

  return (
    <div className="fin-stack insights">
      <div className="row" style={{ gap: 10 }}>
        <span className="small secondary">Месяц расчёта</span>
        <select className="select select--sm turnover-month" value={month ?? ''} onChange={(event) => setChosen(event.target.value)} disabled={choices.length === 0}>
          {choices.length === 0 && <option value="">нет месяца с остатками и продажами</option>}
          {choices.map((item) => (
            <option key={item} value={item}>
              {monthLabel(item)}
              {item === thisMonth ? ' — не закончен' : ''}
            </option>
          ))}
        </select>
        <span className="tiny muted">в списке только месяцы, где есть и снимок остатков, и себестоимость продаж</span>
      </div>

      <div className="kpi-grid">
        <Kpi index={0} state={state} label="Средний запас" value={at ? at.stock : null} format={money} memoryKey="to-stock" hint={month ? `за ${monthLabel(month)}, по снимкам месяца` : noMonthHint} />
        <Kpi index={1} state={state} label="Продано по себестоимости" value={at ? at.cogs : null} format={money} memoryKey="to-cogs" hint={month ? `за ${monthLabel(month)}` : undefined} />
        <Kpi
          index={2}
          state={state}
          label="Дней запаса"
          value={currentDays}
          format={days}
          memoryKey="to-days"
          hint={previousDays !== null ? `месяцем раньше — ${days(previousDays)}` : 'на столько дней продаж хватит среднего запаса'}
        />
        <Kpi
          index={3}
          state={state}
          label="GMROI за месяц"
          value={gmroi}
          format={(value) => `${ratio(value)} ₽`}
          memoryKey="to-gmroi"
          hint="валовой прибыли на рубль среднего запаса; позиции без себестоимости в прибыль не входят"
        />
      </div>

      <Panel
        index={1}
        title="Дни запаса по месяцам"
        subtitle="Средний запас ÷ себестоимость продаж × дней в месяце · месяц без снимка остатков или без продаж пустой"
        states={[months]}
        skeleton={<SkeletonColumns height={190} />}
        empty={{ when: noMonth, title: 'Оборачиваемость посчитать не из чего', hint: noMonthHint }}
      >
        {() => (
          <ColumnChart
            ariaLabel="Дни запаса по месяцам"
            height={190}
            format={days}
            series={[{ key: 'days', label: 'Дней запаса', color: 'var(--series-1)' }]}
            points={trendMonths.map((item) => {
              const cover = byMonth.get(item)
              const value = daysOf(cover)
              return {
                key: item,
                label: shortMonth(item),
                title:
                  value === null
                    ? `${monthLabel(item)} · ${cover && cover.stock > 0 ? 'продаж с себестоимостью нет' : 'снимка остатков нет'}`
                    : `${monthLabel(item)} · запас ${money(cover!.stock)}, продано ${money(cover!.cogs)}`,
                highlight: item === month,
                values: [value],
              }
            })}
          />
        )}
      </Panel>

      <Panel
        index={2}
        title="Позиции"
        subtitle={
          order === 'lowgm'
            ? `Меньше всего прибыли на рубль запаса · ${month ? monthLabel(month) : ''}`
            : `${order === 'slow' ? 'Дольше всего лежат' : 'Быстрее всего уходят'} · дней запаса за ${month ? monthLabel(month) : ''}`
        }
        states={noMonth ? [months] : [months, items]}
        skeleton={<SkeletonRows rows={8} />}
        empty={noMonth ? { when: true, title: 'Нет месяца для расчёта', hint: noMonthHint } : { when: withMetrics.length === 0, title: 'Позиций с продажами и запасом нет' }}
        actions={
          <Segmented
            size="sm"
            label="Порядок"
            value={order}
            onChange={setOrder}
            options={[
              { value: 'slow', label: 'Медленные' },
              { value: 'fast', label: 'Быстрые' },
              { value: 'lowgm', label: 'Низкий GMROI' },
            ]}
          />
        }
      >
        {() => (
          <>
            {idle.length > 0 && (
              <Caveat tone="warn">
                {pluralize(idle.length, 'позиция', 'позиции', 'позиций')} на {money(idleStock)} лежали на складе весь месяц без единой продажи с
                себестоимостью — в список они не входят, дни запаса у них бесконечны. Их перечень — в разделе «Запасы», признак «Без продаж».
              </Caveat>
            )}
            <BarRows
              ariaLabel="Позиции по оборачиваемости"
              series={[{ key: 'v', label: order === 'lowgm' ? 'GMROI' : 'Дней запаса', color: order === 'lowgm' ? 'var(--series-3)' : 'var(--series-1)' }]}
              valueWidth={86}
              rows={sorted.slice(0, 12).map((row) => ({
                key: row.key,
                label: row.label,
                sub: `запас ${money(row.stock)} · продано ${money(row.cogs)}${order === 'lowgm' ? ` · ${days(row.days)}` : ` · GMROI ${ratio(row.gmroi)}`}`,
                values: [order === 'lowgm' ? row.gmroi : row.days],
                format: (value: number) => (order === 'lowgm' ? ratio(value) : days(value)),
              }))}
            />
            {sorted.length > 12 && (
              <div className="tiny muted" style={{ marginTop: 8 }}>
                Показаны 12 из {sorted.length}. Полный список — в отчёте «Оборачиваемость и рентабельность запасов» за {month ? monthLabel(month) : 'месяц'}.
              </div>
            )}
          </>
        )}
      </Panel>

      <DataNotes states={[months, items]} />
    </div>
  )
}
